import React, {
  createContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { ChatsType, MessageType } from '@/types/apps/chat.ts';
import { apiService } from '@/services/api';
import { TicketMessage } from '@/types/apps/ticket';
import { useAuth } from '@/context/AuthContext';

// Define context props interface
export interface ChatContextProps {
  chatContent: any[];
  chatSearch: string;
  selectedChat: ChatsType | null;
  loading: boolean;
  error: string;
  activeChatId: number | null;
  setChatContent: Dispatch<SetStateAction<any[]>>;
  setChatSearch: Dispatch<SetStateAction<string>>;
  setSelectedChat: Dispatch<SetStateAction<ChatsType | null>>;
  setActiveChatId: Dispatch<SetStateAction<number | null>>;
  sendMessage: (chatId: number | string, message: MessageType) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string>>;
  loadTicketMessages: (
    ticketId: string,
    ticketInfo?: {
      subject?: string;
      status?: string;
      supportType?: string;
      customer?: { companyName?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null; creditsAmount?: number | null; customerTypeName?: string | null } | null;
    }
  ) => Promise<void>;
  sendTicketMessage: (
    ticketId: string,
    message: string,
    file?: File
  ) => Promise<void>;
  loadFileMessages: (
    fileId: string,
    fileInfo?: {
      name?: string;
      status?: string;
      customer?: {
        companyName?: string | null;
        firstName: string;
        lastName: string;
        email: string;
        creditsAmount?: number | null;
      } | null;
    }
  ) => Promise<void>;
  sendFileMessage: (
    fileId: string,
    data: { message?: string; file?: File }
  ) => Promise<void>;
}

// Create the context
export const ChatContext = createContext<ChatContextProps>({
  chatContent: [],
  chatSearch: '',
  selectedChat: null,
  loading: true,
  error: '',
  activeChatId: null,
  setChatContent: () => {},
  setChatSearch: () => {},
  setSelectedChat: () => {},
  setActiveChatId: () => {},
  sendMessage: () => {},
  setLoading: () => {},
  setError: () => {},
  loadTicketMessages: async () => {},
  sendTicketMessage: async () => {},
  loadFileMessages: async () => {},
  sendFileMessage: async () => {},
});

// Create the provider component
export const ChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [chatContent, setChatContent] = useState<any[]>([]);
  const [chatSearch, setChatSearch] = useState<string>('');
  const [selectedChat, setSelectedChat] = useState<ChatsType | null>(null);
  const [activeChatId, setActiveChatId] = useState<number | null>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Fetch chat data from the AP

  // Function to send a message to a chat identified by `chatId` using an API call.
  const sendMessage = async () => {};

  // Helper function to determine if message is from current user
  const isMyMessage = (messageSender: string): boolean => {
    if (!user) return false;

    const normalizedSender = messageSender?.toLowerCase?.() || '';

    if (normalizedSender === 'customer' && user.userType === 'customer') {
      return true;
    }

    if (normalizedSender !== 'customer' && user.userType === 'admin') {
      return true;
    }

    return false;
  };

  // Function to load ticket messages
  const loadTicketMessages = async (
    ticketId: string,
    ticketInfo?: {
      subject?: string;
      status?: string;
      supportType?: string;
      customer?: { companyName?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null; creditsAmount?: number | null; customerTypeName?: string | null } | null;
    }
  ) => {
    try {
      setLoading(true);
      setError('');

      const messages: TicketMessage[] =
        await apiService.getTicketMessages(ticketId);

      // Convert ticket messages to chat format
      const chatMessages: MessageType[] = messages.map(msg => ({
        id: msg.id,
        msg: msg.message,
        senderId: isMyMessage(msg.sender) ? 'me' : 'other',
        type: msg.fileUrl ? 'file' : 'text',
        createdAt: msg.createdAt,
        attachment: msg.fileUrl
          ? [
              {
                file: msg.fileUrl,
                fileOriginalName: msg.fileOriginalName ?? undefined,
              },
            ]
          : [],
      }));

      // selectedChat.customer from ticketInfo (passed from table) — for header tooltip
      const ticketChat: ChatsType = {
        id: 'other',
        name: ticketInfo?.subject ?? `Ticket #${ticketId.slice(0, 8)}`,
        status: ticketInfo?.status ?? 'Support',
        thumb: '',
        recent: false,
        excerpt: ticketInfo?.supportType ?? '',
        messages: chatMessages,
        customer: ticketInfo?.customer ?? null,
      };

      setSelectedChat(ticketChat);
      setChatContent(chatMessages);
    } catch (err: any) {
      console.error('Error loading ticket messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Function to send ticket message
  const sendTicketMessage = async (
    ticketId: string,
    message: string,
    file?: File
  ) => {
    try {
      const newMessage = await apiService.sendTicketMessage(ticketId, {
        message,
        file,
      });

      // Convert new message to chat format
      const chatMessage: MessageType = {
        id: newMessage.id,
        msg: newMessage.message,
        // Message from current user is always 'me'
        senderId: 'me',
        type: newMessage.fileUrl ? 'file' : 'text',
        createdAt: newMessage.createdAt,
        attachment: newMessage.fileUrl
          ? [
              {
                file: newMessage.fileUrl,
                fileOriginalName:
                  (newMessage.fileOriginalName ?? file?.name) ?? undefined,
              },
            ]
          : [],
      };

      // Add new message to chat
      if (selectedChat) {
        const updatedMessages = [...selectedChat.messages, chatMessage];
        setSelectedChat({
          ...selectedChat,
          messages: updatedMessages,
        });
        setChatContent(updatedMessages);
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  const loadFileMessages = async (
    fileId: string,
    fileInfo?: {
      name?: string;
      status?: string;
      customer?: {
        companyName?: string | null;
        firstName: string;
        lastName: string;
        email: string;
        creditsAmount?: number | null;
      } | null;
    },
  ) => {
    try {
      setLoading(true);
      setError('');

      const messages = await apiService.getFileMessages(fileId);

      const chatMessages: MessageType[] = messages.map(msg => ({
        id: msg.id,
        msg: msg.message || '',
        senderId: isMyMessage(msg.sender) ? 'me' : 'other',
        type: msg.fileUrl ? 'file' : 'text',
        createdAt: msg.createdAt,
        attachment: msg.fileUrl
          ? [
              {
                file: msg.fileUrl,
                fileOriginalName: msg.fileOriginalName ?? undefined,
              },
            ]
          : [],
      }));

      const fileChat: ChatsType = {
        id: fileId,
        name: fileInfo?.name || `File ${fileId.slice(0, 8)}`,
        status: fileInfo?.status || 'Processing',
        thumb: '',
        recent: false,
        excerpt: '',
        messages: chatMessages,
        customer: fileInfo?.customer || null,
      };

      setSelectedChat(fileChat);
      setChatContent(chatMessages);
    } catch (err: any) {
      console.error('Error loading file messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendFileMessage = async (
    fileId: string,
    data: { message?: string; file?: File },
  ) => {
    if (!data.message && !data.file) {
      throw new Error('Message or file is required.');
    }

    try {
      const newMessage = await apiService.sendFileMessage(fileId, data);

      const chatMessage: MessageType = {
        id: newMessage.id,
        msg: newMessage.message || '',
        senderId: 'me',
        type: newMessage.fileUrl ? 'file' : 'text',
        createdAt: newMessage.createdAt,
        attachment: newMessage.fileUrl
          ? [
              {
                file: newMessage.fileUrl,
                fileOriginalName:
                  (newMessage.fileOriginalName ?? data.file?.name) ?? undefined,
              },
            ]
          : [],
      };

      if (selectedChat) {
        const updatedMessages = [...selectedChat.messages, chatMessage];
        setSelectedChat({
          ...selectedChat,
          messages: updatedMessages,
        });
        setChatContent(updatedMessages);
      }
    } catch (err: any) {
      console.error('Error sending file message:', err);
      throw err;
    }
  };

  const value: ChatContextProps = {
    chatContent,
    chatSearch,
    selectedChat,
    loading,
    error,
    activeChatId,
    setChatContent,
    setChatSearch,
    setSelectedChat,
    setActiveChatId,
    sendMessage,
    setError,
    setLoading,
    loadTicketMessages,
    sendTicketMessage,
    loadFileMessages,
    sendFileMessage,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
